import { formatMoney } from "@/lib/market";
import { Link } from "react-router-dom";
import { ShoppingCart, Trash2, ArrowRight, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useCart, CartItem } from "@/hooks/useCart";
import { cn } from "@/lib/utils";
import { useEffect, useRef, useState } from "react";

interface MiniCartProps {
  itemCount: number;
}

interface GroupedProduct {
  productId: string;
  productName: string;
  productType: "equipment" | "consumable";
  imageUrl?: string;
  items: CartItem[];
  groupSubtotal: number;
}

function groupItemsByProduct(items: CartItem[]): GroupedProduct[] {
  const groups = new Map<string, GroupedProduct>();
  
  items.forEach((item) => {
    const key = `${item.productId}-${item.productType}`;
    
    if (!groups.has(key)) {
      // Extract base product name (without variant suffix)
      const baseName = item.variantName 
        ? item.name.replace(` - ${item.variantName}`, "").replace(` ${item.variantName}`, "")
        : item.name;
      
      groups.set(key, {
        productId: item.productId,
        productName: baseName || item.name,
        productType: item.productType,
        imageUrl: item.imageUrl,
        items: [],
        groupSubtotal: 0,
      });
    }
    
    const group = groups.get(key)!;
    group.items.push(item);
    group.groupSubtotal += item.price * item.quantity;
    
    // Use the first available image
    if (!group.imageUrl && item.imageUrl) {
      group.imageUrl = item.imageUrl;
    }
  });
  
  return Array.from(groups.values());
}

function formatCurrency(value: number): string {
  return formatMoney(value);
}

export function MiniCart({ itemCount }: MiniCartProps) {
  const { items, removeItem, subtotal } = useCart();
  const [isPulsing, setIsPulsing] = useState(false);
  const prevItemCount = useRef(itemCount);

  // Pulse animation when items are added
  useEffect(() => {
    if (itemCount > prevItemCount.current) {
      setIsPulsing(true);
      const timer = setTimeout(() => setIsPulsing(false), 1000);
      return () => clearTimeout(timer);
    }
    prevItemCount.current = itemCount;
  }, [itemCount]);

  const groupedProducts = groupItemsByProduct(items);
  // Show only latest 3 product groups
  const displayGroups = groupedProducts.slice(-3).reverse();
  const hiddenGroupsCount = Math.max(0, groupedProducts.length - 3);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative text-foreground/70 hover:text-primary"
          aria-label={`Carrinho de compras${itemCount > 0 ? `, ${itemCount} ${itemCount === 1 ? 'item' : 'itens'}` : ', vazio'}`}
        >
          <ShoppingCart className={cn("h-5 w-5", isPulsing && "text-primary")} aria-hidden="true" />
          {itemCount > 0 && (
            <span 
              className={cn(
                "absolute -top-1 -right-1 h-5 w-5 rounded-full bg-primary text-[10px] font-semibold text-primary-foreground flex items-center justify-center",
                isPulsing ? "animate-bounce" : "animate-in zoom-in-50 duration-200"
              )}
              aria-hidden="true"
            >
              {itemCount > 99 ? "99+" : itemCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      
      <PopoverContent align="end" className="w-96 p-0" sideOffset={8}>
        {items.length === 0 ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-muted flex items-center justify-center mb-4">
              <ShoppingCart className="h-8 w-8 text-muted-foreground" />
            </div>
            <p className="font-medium mb-1">Carrinho vazio</p>
            <p className="text-sm text-muted-foreground mb-4">
              Ainda não adicionou produtos
            </p>
            <Button asChild size="sm">
              <Link to="/loja">Explorar Loja</Link>
            </Button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="px-4 py-3 border-b bg-muted/30">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">Carrinho</h3>
                <span className="text-sm text-muted-foreground">
                  {itemCount} {itemCount === 1 ? "item" : "itens"}
                </span>
              </div>
            </div>
            
            {/* Product groups */}
            <ScrollArea className="max-h-[320px]">
              <div className="divide-y">
                {displayGroups.map((group) => (
                  <div key={group.productId} className="p-4">
                    {/* Product header with image */}
                    <div className="flex gap-3 mb-3">
                      <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted flex-shrink-0 border">
                        {group.imageUrl ? (
                          <img
                            src={group.imageUrl}
                            alt={group.productName}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package className="h-6 w-6 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-sm leading-tight line-clamp-2">
                          {group.productName}
                        </h4>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {group.productType === "equipment" ? "Equipamento" : "Consumível"}
                        </p>
                      </div>
                    </div>
                    
                    {/* Variant items */}
                    <div className="space-y-2 ml-2 pl-3 border-l-2 border-muted">
                      {group.items.map((item) => (
                        <div 
                          key={item.id} 
                          className="flex items-center justify-between text-sm group"
                        >
                          <div className="flex-1 min-w-0">
                            <span className={cn(
                              "text-muted-foreground",
                              !item.variantName && "font-medium text-foreground"
                            )}>
                              {item.variantName || "Unidade"}
                            </span>
                            <span className="text-muted-foreground mx-1">×</span>
                            <span className="font-medium">{item.quantity}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium tabular-nums">
                              {formatCurrency(item.price * item.quantity)}
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                              onClick={() => removeItem(item.id)}
                              aria-label={`Remover ${item.variantName || item.name}`}
                            >
                              <Trash2 className="h-3 w-3" aria-hidden="true" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Group subtotal (only if multiple items) */}
                    {group.items.length > 1 && (
                      <div className="flex justify-end mt-2 pt-2 border-t border-dashed">
                        <span className="text-xs text-muted-foreground mr-2">Subtotal:</span>
                        <span className="text-sm font-semibold">
                          {formatCurrency(group.groupSubtotal)}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>
            
            {/* Hidden groups indicator */}
            {hiddenGroupsCount > 0 && (
              <div className="px-4 py-2 bg-muted/30 border-t">
                <p className="text-xs text-muted-foreground text-center">
                  +{hiddenGroupsCount} {hiddenGroupsCount === 1 ? "produto" : "produtos"}
                </p>
              </div>
            )}
            
            <Separator />
            
            {/* Summary */}
            <div className="p-4 space-y-3 bg-card">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="text-lg font-bold tabular-nums">
                  {formatCurrency(subtotal)}
                </span>
              </div>
              
              <div className="text-xs text-muted-foreground text-right">
                Portes calculados no checkout
              </div>
              
              <div className="grid grid-cols-2 gap-2 pt-1">
                <Button asChild variant="outline" size="sm">
                  <Link to="/carrinho">Ver Carrinho</Link>
                </Button>
                <Button asChild size="sm">
                  <Link to="/checkout" className="gap-1">
                    Checkout <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </Button>
              </div>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
